/**
 * Web Vitals Monitoring
 * Tracks Core Web Vitals (LCP, INP, CLS) and navigation timing per feature flag state
 */

import { onCLS, onINP, onLCP, onFCP, onTTFB, Metric } from 'web-vitals';

interface WebVitalsPayload {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  navigationType: string;
  // Additional context
  route: string;
  ionicShellEnabled: boolean;
  ionicRouteEnabled: boolean;
  userRole?: string;
  timestamp: number;
}

interface NavigationTiming {
  route: string;
  duration: number;
  ionicShellEnabled: boolean;
  timestamp: number;
}

// Queue for batching metrics (send every 30s or 10 metrics)
let metricsQueue: WebVitalsPayload[] = [];
let navigationQueue: NavigationTiming[] = [];
let flushTimer: NodeJS.Timeout | null = null;

const BATCH_SIZE = 10;
const BATCH_INTERVAL_MS = 30000; // 30 seconds

/**
 * Send metrics to analytics endpoint (or console in dev)
 */
function sendMetrics(metrics: WebVitalsPayload[], navigations: NavigationTiming[]) {
  if (metrics.length === 0 && navigations.length === 0) return;

  // In production, send to analytics endpoint
  if (import.meta.env.PROD) {
    // TODO: Send to analytics service (e.g., Google Analytics, DataDog, custom endpoint)
    // fetch('/api/analytics/web-vitals', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({ metrics, navigations }),
    // }).catch(err => console.warn('Failed to send web vitals:', err));
    
    // For now, use console.log with special format for log aggregation
    console.log('[WEB_VITALS]', JSON.stringify({ metrics, navigations }));
  } else {
    // Development: log to console
    if (metrics.length > 0) {
      console.group('🔍 Web Vitals');
      metrics.forEach(metric => {
        const icon = metric.rating === 'good' ? '✅' : metric.rating === 'needs-improvement' ? '⚠️' : '❌';
        console.log(`${icon} ${metric.name}: ${metric.value.toFixed(2)}ms (${metric.rating})`);
        console.log(`  Route: ${metric.route} | Ionic: ${metric.ionicShellEnabled}/${metric.ionicRouteEnabled}`);
      });
      console.groupEnd();
    }
    
    if (navigations.length > 0) {
      console.group('⏱️ Navigation Timing');
      navigations.forEach(nav => {
        console.log(`${nav.route}: ${nav.duration.toFixed(2)}ms (Ionic: ${nav.ionicShellEnabled})`);
      });
      console.groupEnd();
    }
  }
}

/**
 * Flush queued metrics
 */
function flushMetrics() {
  if (metricsQueue.length > 0 || navigationQueue.length > 0) {
    sendMetrics([...metricsQueue], [...navigationQueue]);
    metricsQueue = [];
    navigationQueue = [];
  }
  
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}

/**
 * Queue a metric for batching
 */
function queueMetric(metric: WebVitalsPayload) {
  metricsQueue.push(metric);
  
  // Flush if batch size reached
  if (metricsQueue.length >= BATCH_SIZE) {
    flushMetrics();
  } else {
    // Schedule flush if not already scheduled
    if (!flushTimer) {
      flushTimer = setTimeout(flushMetrics, BATCH_INTERVAL_MS);
    }
  }
}

/**
 * Queue a navigation timing
 */
function queueNavigation(navigation: NavigationTiming) {
  navigationQueue.push(navigation);
  
  // Flush with same logic as metrics
  if (navigationQueue.length >= BATCH_SIZE) {
    flushMetrics();
  } else if (!flushTimer) {
    flushTimer = setTimeout(flushMetrics, BATCH_INTERVAL_MS);
  }
}

/**
 * Initialize web vitals monitoring
 */
export function initWebVitalsMonitoring(
  getCurrentRoute: () => string,
  getFeatureFlagState: () => { ionicShellEnabled: boolean; ionicRouteEnabled: boolean },
  getUserRole?: () => string | undefined
) {
  // Monitor Core Web Vitals
  const handleMetric = (metric: Metric) => {
    const flagState = getFeatureFlagState();
    
    const payload: WebVitalsPayload = {
      name: metric.name,
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta,
      id: metric.id,
      navigationType: metric.navigationType,
      route: getCurrentRoute(),
      ionicShellEnabled: flagState.ionicShellEnabled,
      ionicRouteEnabled: flagState.ionicRouteEnabled,
      userRole: getUserRole?.(),
      timestamp: Date.now(),
    };
    
    queueMetric(payload);
  };

  // Register Core Web Vitals observers
  onCLS(handleMetric);
  onINP(handleMetric);
  onLCP(handleMetric);
  onFCP(handleMetric);
  onTTFB(handleMetric);

  // Flush metrics on page unload
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushMetrics();
    }
  });

  // Flush on page unload
  window.addEventListener('pagehide', flushMetrics);

  if (import.meta.env.DEV) {
    console.debug('Web Vitals monitoring initialized');
  }
}

/**
 * Track navigation timing manually (for SPA route changes)
 */
export function trackNavigation(
  route: string,
  duration: number,
  ionicShellEnabled: boolean
) {
  queueNavigation({
    route,
    duration,
    ionicShellEnabled,
    timestamp: Date.now(),
  });
}

/**
 * Track feature flag exposure for analytics
 */
export function trackFlagExposure(
  flagKey: string,
  flagValue: boolean | string,
  userRole?: string
) {
  if (import.meta.env.PROD) {
    // Log for analytics aggregation
    console.log('[FLAG_EXPOSURE]', JSON.stringify({
      flagKey,
      flagValue,
      userRole,
      route: window.location.pathname,
      timestamp: Date.now(),
    }));
  }
}

/**
 * Manual flush (useful for testing or critical moments)
 */
export function flushWebVitals() {
  flushMetrics();
}
